import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tasksService } from '../../services/api';

// Async thunks for API calls
export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (params, { rejectWithValue }) => {
    try {
      const response = await tasksService.getProjects(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchProject = createAsyncThunk(
  'projects/fetchProject',
  async (id, { rejectWithValue }) => {
    try {
      const response = await tasksService.getProject(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createProject = createAsyncThunk(
  'projects/createProject',
  async (projectData, { rejectWithValue }) => {
    try {
      const response = await tasksService.createProject(projectData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await tasksService.updateProject(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (id, { rejectWithValue }) => {
    try {
      await tasksService.deleteProject(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  projects: [],
  selectedProject: null,
  filters: {
    search: '',
    status: [],
    customer: '',
    dateRange: null,
    source: ''
  },
  sorting: {
    field: 'created_at',
    direction: 'desc'
  },
  pagination: {
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  },
  loading: false,
  error: null
};

// Slice
const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset to first page when filters change
    },
    setSorting: (state, action) => {
      state.sorting = action.payload;
      state.pagination.page = 1; // Reset to first page when sorting changes
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateProjectRealtime: (state, action) => {
      const updatedProject = action.payload;
      const index = state.projects.findIndex(project => project.id === updatedProject.id);
      if (index !== -1) {
        state.projects[index] = updatedProject;
      }
      if (state.selectedProject && state.selectedProject.id === updatedProject.id) {
        state.selectedProject = updatedProject;
      }
    },
    addProjectRealtime: (state, action) => {
      const newProject = action.payload;
      state.projects.unshift(newProject);
      state.pagination.total += 1;
    },
    deleteProjectRealtime: (state, action) => {
      const projectId = action.payload;
      state.projects = state.projects.filter(project => project.id !== projectId);
      if (state.selectedProject && state.selectedProject.id === projectId) {
        state.selectedProject = null;
      }
      state.pagination.total -= 1;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch projects
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload.results || action.payload;
        if (action.payload.count !== undefined) {
          state.pagination.total = action.payload.count;
          state.pagination.totalPages = Math.ceil(action.payload.count / state.pagination.limit);
        }
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch single project
      .addCase(fetchProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProject = action.payload;
        // Update project in list if it exists
        const index = state.projects.findIndex(project => project.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
      })
      .addCase(fetchProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create project
      .addCase(createProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects.unshift(action.payload);
        state.pagination.total += 1;
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update project
      .addCase(updateProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.projects.findIndex(project => project.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
        if (state.selectedProject && state.selectedProject.id === action.payload.id) {
          state.selectedProject = action.payload;
        }
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete project
      .addCase(deleteProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.filter(project => project.id !== action.payload);
        if (state.selectedProject && state.selectedProject.id === action.payload) {
          state.selectedProject = null;
        }
        state.pagination.total -= 1;
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Export actions
export const {
  setFilters,
  setSorting,
  setPagination,
  clearFilters,
  clearError,
  updateProjectRealtime,
  addProjectRealtime,
  deleteProjectRealtime
} = projectsSlice.actions;

// Selectors
export const selectProjects = (state) => state.projects.projects;
export const selectSelectedProject = (state) => state.projects.selectedProject;
export const selectProjectsLoading = (state) => state.projects.loading;
export const selectProjectsError = (state) => state.projects.error;
export const selectProjectsFilters = (state) => state.projects.filters;
export const selectProjectsSorting = (state) => state.projects.sorting;
export const selectProjectsPagination = (state) => state.projects.pagination;

// Computed selectors
export const selectFilteredProjectsCount = (state) => state.projects.pagination.total;

export default projectsSlice.reducer;